import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useFeePolicy } from 'reservations/account/providers/ConfigProvider';
import type { FeePolicyConfig } from 'reservations/account/types';
import { isValidPhoneNumber } from 'reservations/base/helpers/phone';
import { RESERVATION, WAITPARTY, WALKIN } from 'reservations/booking/types';
import { useDefaultBookingFormValues } from 'reservations/form/hooks/useFormValues';

const cardDetailsSchema = z.object({
  address_zip: z.string(),
  brand: z.string(),
  first6: z.string().min(6),
  last4: z.string().min(4),
  exp_month: z.string(),
  exp_year: z.string()
});

export const bookingFormSchema = (feePolicy?: FeePolicyConfig) =>
  z
    .object({
      date: z.date(),
      partySize: z.number(),
      name: z.string().min(1, { message: 'Name is required' }),
      // TODO: Replace .string() with .email() when upgrading to Zod v4
      email: z
        .string()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Enter a valid email address' }),
      specialOccasion: z.string(),
      note: z
        .string()
        .max(500, { message: 'Notes cannot be longer than 500 characters' }),
      phone: z.string().min(1),
      phoneCode: z.string().min(1),
      fullPhoneNumber: z
        .string()
        .min(1, { message: 'Phone number is required' })
        .refine(isValidPhoneNumber, {
          message: 'Enter a valid phone number'
        }),
      optInMarketingEmail: z.boolean(),
      optInMarketingSms: z.boolean(),
      time: z.number().nullish(),
      availability: z.string().nullish(),
      // TODO: Replace with .literal([RESERVATION, WAITPARTY, WALKIN]) when upgrading to Zod v4
      bookingType: z
        .literal(RESERVATION)
        .or(z.literal(WAITPARTY))
        .or(z.literal(WALKIN))
        .default(RESERVATION),
      savedPaymentMethod: z.string().default(''),
      token: z.string().default(''),
      cardDetails: cardDetailsSchema.nullish().default(null),
      optInChecklessPayments: z.boolean().default(false),
      tipPercentage: z.number().int().nonnegative().default(0),
      guests: z.array(
        z.object({ id: z.number().int(), inviteToSplit: z.boolean() })
      )
    })
    .superRefine((data, ctx) => {
      if (
        feePolicy?.shouldCollect &&
        data.partySize >= feePolicy.minPartySize
      ) {
        const hasToken = !!data.token;
        const hasSaved = !!data.savedPaymentMethod;
        if (!hasToken && !hasSaved) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['token'],
            message:
              'Credit card required. Please add your payment information to complete this reservation.'
          });
        }
      }
    });

export type BookingFormSchema = z.infer<ReturnType<typeof bookingFormSchema>>;

type FormProps = {
  defaultFormValues?: BookingFormSchema;
  children?: ReactNode;
};

function useReservationsForm(defaultFormValues?: BookingFormSchema) {
  const defaultValues = useDefaultBookingFormValues(defaultFormValues);
  const feePolicy = useFeePolicy();

  return useForm<BookingFormSchema>({
    resolver: zodResolver(bookingFormSchema(feePolicy)),
    defaultValues
  });
}

const Form = ({ children, defaultFormValues }: FormProps) => {
  const methods = useReservationsForm(defaultFormValues);
  return (
    <FormProvider data-test-id="form-provider" {...methods}>
      <form data-testid="form">{children}</form>
    </FormProvider>
  );
};

export default Form;
